import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaWhatsapp } from "react-icons/fa";
import {
  listWhatsAppWebSegments,
  listWhatsAppWebTemplates,
  dispatchProductToWhatsApp,
} from "../../services/api";

/**
 * Modal pra disparar oferta de Product cadastrado em grupos WhatsApp.
 * Conecta sistema atual de Products ao novo WhatsApp Web.
 */
export default function WhatsAppDispatchModal({ show, productId, productName, onClose }) {
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [whenMode, setWhenMode] = useState("now");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [useAffiliate, setUseAffiliate] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!show) return;
    setFeedback(null);
    Promise.all([listWhatsAppWebSegments(), listWhatsAppWebTemplates()]).then(
      ([s, t]) => {
        setSegments(s?.data || []);
        setTemplates(t?.data || []);
      }
    );
  }, [show]);

  function buildScheduledAt() {
    if (whenMode !== "schedule" || !scheduleDate || !scheduleTime) return null;
    return `${scheduleDate}T${scheduleTime}:00`;
  }

  async function handleSend() {
    if (!segmentId) return alert("Selecione um segmento.");
    setLoading(true);
    setFeedback(null);
    try {
      await dispatchProductToWhatsApp(productId, {
        segment_id: segmentId,
        template_id: templateId || null,
        scheduled_at: buildScheduledAt(),
        use_affiliate_conversion: useAffiliate,
        custom_message: customMessage.trim() || null,
      });
      setFeedback({
        type: "success",
        text: buildScheduledAt()
          ? "✅ Oferta agendada com sucesso!"
          : "✅ Oferta enviada e está sendo processada! Acompanhe em /admin/whatsapp-web/campanhas",
      });
      setSegmentId("");
      setTemplateId("");
      setCustomMessage("");
      setWhenMode("now");
    } catch (err) {
      setFeedback({ type: "danger", text: "Falha ao enviar oferta." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaWhatsapp style={{ color: "#25D366" }} /> Disparar pra WhatsApp
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {productName && (
          <p className="text-muted mb-3">
            Oferta: <strong>{productName}</strong>
          </p>
        )}

        {feedback && (
          <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        )}

        <Form.Group>
          <Form.Label>Segmento *</Form.Label>
          <Form.Control
            as="select"
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.total_groups} grupos)
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group>
          <Form.Label>Template (opcional)</Form.Label>
          <Form.Control
            as="select"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">Sem template (texto padrão Caçadores)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} [{t.template_model}]
              </option>
            ))}
          </Form.Control>
          <Form.Text className="text-muted">
            Templates podem usar variáveis de IA (Gemini gera o texto).
          </Form.Text>
        </Form.Group>

        <Form.Group>
          <Form.Label>Mensagem customizada (opcional, sobrescreve template)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Deixe vazio pra usar template ou texto padrão"
          />
        </Form.Group>

        <Form.Check
          type="checkbox"
          label="Converter link pra afiliado (Shopee/Amazon API)"
          checked={useAffiliate}
          onChange={(e) => setUseAffiliate(e.target.checked)}
        />

        <hr />
        <h6>📅 Quando enviar?</h6>
        <Form.Check
          type="radio"
          label="🔥 Enviar Agora"
          checked={whenMode === "now"}
          onChange={() => setWhenMode("now")}
        />
        <Form.Check
          type="radio"
          label="📅 Agendar"
          checked={whenMode === "schedule"}
          onChange={() => setWhenMode("schedule")}
        />
        {whenMode === "schedule" && (
          <div className="d-flex mt-2" style={{ gap: 10 }}>
            <Form.Control
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
            />
            <Form.Control
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSend} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : <FaWhatsapp />}{" "}
          {loading ? "Enviando..." : "Disparar pra WhatsApp"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
